import { clearCache } from "../lib/pageCache";
import { createContext, useContext, useEffect, useState } from "react";
import { authApi, toggleFavorite as apiToggleFavorite } from "../lib/api";
import { kidApi } from "../lib/kids";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out

  useEffect(() => {
    authApi
      .me()
      .catch(() => authApi.refresh())
      .then(setUser)
      .catch(() => kidApi.me().then((k) => setUser({ ...k, role: "kid" })))
      .catch(() => setUser(false));
    const onExpired = () => { clearCache(); setUser(false); };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  const themePref = user?.prefs?.theme;
  useEffect(() => {
    if (!themePref) return;
    const v = themePref === "system" ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : themePref;
    localStorage.setItem("theme", v);
    document.documentElement.classList.toggle("dark", v === "dark");
    window.dispatchEvent(new CustomEvent("theme:set", { detail: v }));
  }, [themePref]);

  const value = {
    user,
    setUser,
    isFavorite: (id) => !!user?.favorite_plant_ids?.includes(id),
    toggleFavorite: async (id) => {
      const r = await apiToggleFavorite(id);
      setUser((u) => ({ ...u, favorite_plant_ids: r.favorite_plant_ids }));
      return r.favorite;
    },
    login: async (email, password) => {
      const u = await authApi.login(email, password);
      setUser(u);
      return u;
    },
    register: async (name, email, password, invite) => {
      const u = await authApi.register(name, email, password, invite);
      setUser(u);
      return u;
    },
    kidLogin: async (username, pictures) => {
      const k = await kidApi.login(username, pictures);
      clearCache();
      setUser({ ...k, role: "kid" });
      return k;
    },
    logout: async () => {
      if (user?.role === "kid") await kidApi.logout().catch(() => {});
      else await authApi.logout().catch(() => {});
      clearCache();
      setUser(false);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
